import { useUser } from "@clerk/clerk-react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useImpersonate } from "@/hooks/useImpersonate";
import { usePermissions } from "@/hooks/usePermissions";
import type { User } from "@/interfaces/user";

interface OrgUserRowActionsProps {
  user: User;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
}

/** True if `user` is the signed-in caller's own row. */
export function isSelf(user: User, currentClerkUserId: string | null | undefined): boolean {
  return !!currentClerkUserId && user.clerkUserId === currentClerkUserId;
}

/**
 * Row actions for the org → Users panel.
 *
 * Edit/Delete are gated on can("delete","USER") — the one capability CLIENT_ADMIN/GFG_SUPER_ADMIN
 * hold that Manager/View Only/GFG_STANDARD_ADMIN don't, matching "visible only to the Admin role"
 * without letting Manager's separate edit-USER capability leak through. Delete is additionally
 * hidden on the caller's own row.
 *
 * Impersonate is gated separately on can("start","IMPERSONATION") — GFG roles only, including
 * GFG_STANDARD_ADMIN who cannot edit — and follows the Users blotter's rules: never on yourself,
 * and only when Clerk has a session to become (a `clerkUserId`, not archived). It is a
 * convenience for staff looking at a real org or a demo org (Users & Orgs → Demo); an
 * impersonated session carries isGfg=false and never sees it.
 */
export default function OrgUserRowActions({ user, onEdit, onDelete }: OrgUserRowActionsProps) {
  const { can } = usePermissions();
  const { user: currentUser } = useUser();
  const impersonate = useImpersonate();
  const canManage = can("delete", "USER");
  const self = isSelf(user, currentUser?.id);
  const canImpersonate =
    can("start", "IMPERSONATION") && !self && !!user.clerkUserId && !user.archived;

  if (!canManage && !canImpersonate) return null;

  function handleImpersonate() {
    if (!user.clerkUserId) return;
    void impersonate.impersonate(user.clerkUserId);
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      {canImpersonate && (
        <Tooltip title={`Impersonate ${user.name}`}>
          <span style={{ display: "inline-flex" }}>
            <IconButton
              onClick={handleImpersonate}
              size="small"
              disabled={impersonate.isPending}
              aria-label={`Impersonate ${user.name}`}
              sx={{
                width: 34,
                height: 34,
                ml: "6px",
                border: "1px solid var(--color-smoke)",
                borderRadius: "var(--radius-btn)",
                color: "var(--color-dark-dust)",
                "&:hover": { bgcolor: "var(--color-wax)", color: "var(--color-rodeo-black)" },
              }}
            >
              <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {canManage && (
        <Tooltip title="Edit teammate">
          <IconButton
            onClick={() => onEdit(user)}
            size="small"
            aria-label={`Edit ${user.name}`}
            sx={{
              width: 34,
              height: 34,
              ml: "6px",
              border: "1px solid var(--color-smoke)",
              borderRadius: "var(--radius-btn)",
              color: "var(--color-graphite)",
              "&:hover": { bgcolor: "var(--color-wax)", color: "var(--color-rodeo-black)" },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
      {canManage && !self && (
        <Tooltip title="Delete user">
          <IconButton
            onClick={() => onDelete(user)}
            size="small"
            aria-label={`Delete ${user.name}`}
            sx={{
              width: 34,
              height: 34,
              ml: "6px",
              border: "1px solid var(--color-smoke)",
              borderRadius: "var(--radius-btn)",
              color: "var(--color-graphite)",
              "&:hover": {
                bgcolor: "var(--color-good-wine-bg)",
                color: "var(--color-good-wine)",
                borderColor: "var(--color-good-wine-3)",
              },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

/** Small neutral "You" badge for the caller's own row (reference demo's .cs-you-badge). */
export function YouBadge() {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        fontFamily: "var(--font-lato)",
        fontSize: "10px",
        fontWeight: 700,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        color: "var(--color-graphite)",
        bgcolor: "var(--color-smoke)",
        borderRadius: "6px",
        px: "7px",
        py: "2px",
        ml: 1,
        verticalAlign: "middle",
      }}
    >
      You
    </Box>
  );
}
